import { useMemo, useState } from 'react'
import { useReportsPeriodControls } from '../../hooks/useReportsPeriodControls'
import { useStudentInsights } from '../../hooks/useStudentInsights'
import { downloadCsv } from '../../utils/reportExport'

const chartTones = ['purple', 'blue', 'green', 'orange', 'red']
const percentage = (value, total) => (total ? Math.round((value / total) * 100) : 0)
const withTones = (rows, tones = chartTones) =>
	(rows || []).map((row, index) => ({ ...row, tone: tones[index % tones.length] }))

/**
 * All page-level state and derived data for StudentReport.
 * Reporting period comes from shared MobX applied state.
 */
export const useStudentReport = () => {
	const periodControls = useReportsPeriodControls()
	const [grade, setGrade] = useState('all')
	const { status, data, error } = useStudentInsights({
		period: periodControls.appliedPeriod,
		grade,
		customFrom: periodControls.appliedCustomFrom,
		customTo: periodControls.appliedCustomTo,
	})

	const roster = data.roster?.total || 0
	const activeInPeriod = data.roster?.activeInPeriod ?? data.roster?.active90 ?? 0
	const done = data.doneInPeriod || {}
	const appPrep = data.appPrep || {}
	const coverage = data.coverage || {}
	const grades = data.grades || []

	const interestRows = useMemo(() => withTones(data.interestFamilies), [data.interestFamilies])
	const topUniversities = useMemo(() => withTones(data.topUniversities, ['blue']), [data.topUniversities])
	const topCountries = useMemo(() => withTones(data.topCountries, ['green']), [data.topCountries])
	const topCareers = useMemo(() => withTones(data.topCareers), [data.topCareers])
	const personalities = useMemo(() => withTones(data.personality), [data.personality])

	const attentionRows = (data.priorityStudents || []).slice(0, 4).map(student => ({
		id: student.id,
		title: student.name,
		detail: student.reason,
		owner: `Grade ${student.grade} · ${student.counselor}`,
		status: 'Priority',
	}))

	const exportRows = (data.students || []).map(student => ({
		student: student.name,
		grade: student.grade,
		counsellor: student.counselor,
		last_platform_activity: student.lastContact,
		assessment_completed: student.quizCompleted ? 'Yes' : 'No',
		personality_type: student.personalityType || '',
		career_interest: student.interestCategory || '',
		saved_exploration: student.hasSavedExploration ? 'Yes' : 'No',
		assigned_work_completed: `${student.tasksDone}/${student.tasksTotal}`,
		application_preparation: student.applicationStage,
		priority_reason: student.priorityReason || '',
	}))

	const handleExport = () => downloadCsv('student-insights.csv', exportRows)

	return {
		...periodControls,
		period: periodControls.selectedPeriod,
		customFrom: periodControls.draftFrom,
		customTo: periodControls.draftTo,
		setPeriod: periodControls.onPeriodChange,
		setCustomFrom: periodControls.onCustomFromChange,
		setCustomTo: periodControls.onCustomToChange,
		grade,
		setGrade,
		grades,
		status,
		data,
		error,
		roster,
		activeInPeriod,
		done,
		appPrep,
		coverage,
		interestRows,
		topUniversities,
		topCountries,
		topCareers,
		personalities,
		attentionRows,
		handleExport,
		percentage,
	}
}
